import { getApp } from "firebase/app";
import { getDatabase, onValue, ref } from "firebase/database";
import { useEffect, useState } from "react";

import type { EventCollectionId } from "../workspace/collectionIds";
import {
  parseIndividualLeaderboardMeta,
  parseIndividualTop,
  parseTeamLeaderboardMeta,
  parseTeamLeaderboardRows,
  type AdminIndividualRankingRow,
  type AdminLeaderboardMeta,
  type AdminTeamLeaderboardMeta,
  type AdminTeamRankingRow,
} from "./parseEventLeaderboard";

export type EventIndividualLeaderboard = {
  rows: AdminIndividualRankingRow[];
  meta: AdminLeaderboardMeta | null;
  loading: boolean;
  error: string | null;
};

export type EventTeamLeaderboard = {
  rows: AdminTeamRankingRow[];
  meta: AdminTeamLeaderboardMeta | null;
  loading: boolean;
  error: string | null;
};

const eventLeaderboardPath = (
  eventId: EventCollectionId,
  child: string
): string =>
  `leaderboards/${eventId.accountId}/${eventId.eventId}/${child}`;

export function useEventIndividualLeaderboard(
  eventId: EventCollectionId
): EventIndividualLeaderboard {
  const [rows, setRows] = useState<AdminIndividualRankingRow[]>([]);
  const [meta, setMeta] = useState<AdminLeaderboardMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const db = getDatabase(getApp());
    const topRef = ref(db, eventLeaderboardPath(eventId, "individualTop"));
    const metaRef = ref(db, eventLeaderboardPath(eventId, "meta"));

    const unsubTop = onValue(
      topRef,
      (snapshot) => {
        setRows(parseIndividualTop(snapshot.val()));
        setLoading(false);
      },
      (listenError) => {
        setError(listenError.message);
        setLoading(false);
      }
    );

    const unsubMeta = onValue(
      metaRef,
      (snapshot) => {
        setMeta(parseIndividualLeaderboardMeta(snapshot.val()));
      },
      (listenError) => {
        setError(listenError.message);
      }
    );

    return () => {
      unsubTop();
      unsubMeta();
    };
  }, [eventId.accountId, eventId.eventId]);

  return { rows, meta, loading, error };
}

export function useEventTeamLeaderboard(
  eventId: EventCollectionId
): EventTeamLeaderboard {
  const [rows, setRows] = useState<AdminTeamRankingRow[]>([]);
  const [meta, setMeta] = useState<AdminTeamLeaderboardMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const db = getDatabase(getApp());
    const teamsRef = ref(db, eventLeaderboardPath(eventId, "teams"));
    const metaRef = ref(db, eventLeaderboardPath(eventId, "teamMeta"));

    const unsubTeams = onValue(
      teamsRef,
      (snapshot) => {
        setRows(parseTeamLeaderboardRows(snapshot.val()));
        setLoading(false);
      },
      (listenError) => {
        setError(listenError.message);
        setLoading(false);
      }
    );

    const unsubMeta = onValue(
      metaRef,
      (snapshot) => {
        setMeta(parseTeamLeaderboardMeta(snapshot.val()));
      },
      (listenError) => {
        setError(listenError.message);
      }
    );

    return () => {
      unsubTeams();
      unsubMeta();
    };
  }, [eventId.accountId, eventId.eventId]);

  return { rows, meta, loading, error };
}
